'use client';

import { useState } from 'react';
import type { Anniversary } from '@/lib/types';
import {
  calculateAge,
  getDayOfWeek,
  getNextBirthday,
  formatDate,
  formatTime,
} from '@/lib/date-utils';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAnniversaries } from '@/hooks/use-anniversaries';
import { AnniversaryForm } from './anniversary-form';
import {
  Heart,
  Trash2,
  Users,
  StickyNote,
  CalendarClock,
  Bell,
  Pencil,
  Clock,
  ChevronDown,
} from 'lucide-react';
import { differenceInDays, isToday, subDays } from 'date-fns';
import { useNotifications } from '@/hooks/use-notifications';
import { useToast } from '@/hooks/use-toast';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export function AnniversaryCard({ anniversary }: { anniversary: Anniversary }) {
  const { deleteAnniversary } = useAnniversaries();
  const { scheduleNotification } = useNotifications();
  const { toast } = useToast();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const years = calculateAge(anniversary.date);
  const nextAnniversaryDate = getNextBirthday(anniversary.date);
  const nextAnniversaryDay = nextAnniversaryDate ? getDayOfWeek(nextAnniversaryDate) : null;
  const celebrating = years !== null ? years + 1 : null;
  const isAnniversaryToday = nextAnniversaryDate ? isToday(nextAnniversaryDate) : false;

  const daysUntilNext = nextAnniversaryDate
    ? Math.ceil(differenceInDays(nextAnniversaryDate, new Date()))
    : null;

  const countdownText = () => {
    if (daysUntilNext === null) return '';
    if (daysUntilNext === 0) return 'today!';
    if (daysUntilNext === 1) return 'in 1 day';
    return `in ${daysUntilNext} days`;
  };

  const handleSetReminder = (option: 'on-day' | 'day-before' | 'week-before') => {
    if (!nextAnniversaryDate || celebrating === null) {
      toast({
        variant: 'destructive',
        title: 'Cannot Set Reminder',
        description: 'A valid date is required to set a reminder.',
      });
      return;
    }
    
    let reminderDate = nextAnniversaryDate;
    let reminderDescription = `You'll be notified on the day of the anniversary.`;

    switch (option) {
      case 'day-before':
        reminderDate = subDays(nextAnniversaryDate, 1);
        reminderDescription = `You'll be notified one day before the anniversary.`;
        break;
      case 'week-before':
         reminderDate = subDays(nextAnniversaryDate, 7);
         reminderDescription = `You'll be notified one week before the anniversary.`;
        break;
    }


    scheduleNotification(
      reminderDate,
      `Reminder for ${anniversary.name}`,
      `The ${celebrating}th anniversary is coming up. Don't forget to celebrate!`
    );
    toast({
      title: 'Reminder Set!',
      description: reminderDescription,
    });
  };

  return (
    <>
      <Card className="flex flex-col">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                {anniversary.name}
                {isAnniversaryToday && (
                  <span className="text-2xl" role="img" aria-label="Party Popper">
                    🎉
                  </span>
                )}
              </CardTitle>
              {celebrating !== null && (
                <CardDescription>
                  Celebrating <span className="font-semibold">{celebrating}</span> years{' '}
                  {countdownText()}
                </CardDescription>
              )}
            </div>
            {nextAnniversaryDate && (
              <Badge variant={isAnniversaryToday ? 'default' : 'secondary'}>
                {formatDate(nextAnniversaryDate)}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="flex-grow space-y-3">
          {anniversary.date && (
            <>
              <div className="flex items-center text-sm text-muted-foreground">
                <Heart className="mr-2 h-4 w-4" />
                <span>
                  Started on {formatDate(anniversary.date)} (a {getDayOfWeek(anniversary.date)})
                </span>
              </div>
              {anniversary.time && (
                <div className="flex items-center text-sm text-muted-foreground">
                  <Clock className="mr-2 h-4 w-4" />
                  <span>{formatTime(anniversary.time)}</span>
                </div>
              )}
              {nextAnniversaryDay && (
                <div className="flex items-center text-sm text-muted-foreground">
                  <CalendarClock className="mr-2 h-4 w-4" />
                  <span>Next anniversary is on a {nextAnniversaryDay}</span>
                </div>
              )}
            </>
          )}
          <div className="flex items-center text-sm text-muted-foreground">
            <Users className="mr-2 h-4 w-4" />
            <span>Type: {anniversary.type}</span>
          </div>
          {anniversary.notes && (
            <div className="flex items-start text-sm text-muted-foreground">
              <StickyNote className="mr-2 h-4 w-4 flex-shrink-0 mt-1" />
              <p className="border-l-2 pl-3">{anniversary.notes}</p>
            </div>
          )}
        </CardContent>
        <CardFooter className="flex justify-end gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" disabled={!nextAnniversaryDate}>
                <Bell className="mr-2 h-4 w-4" />
                Set Reminder
                <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => handleSetReminder('on-day')}>
                On the day
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleSetReminder('day-before')}>
                1 day before
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleSetReminder('week-before')}>
                1 week before
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm">
                <Pencil className="mr-2 h-4 w-4" />
                Edit
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Edit Anniversary</DialogTitle>
                <DialogDescription>
                  Update the details for the {anniversary.name} anniversary.
                </DialogDescription>
              </DialogHeader>
              <AnniversaryForm
                anniversary={anniversary}
                onSuccess={() => setIsEditModalOpen(false)}
              />
            </DialogContent>
          </Dialog>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" size="sm">
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                <AlertDialogDescription>
                  This action cannot be undone. This will permanently delete the
                  anniversary for {anniversary.name}.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={() => deleteAnniversary(anniversary.id)}>
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </CardFooter>
      </Card>
    </>
  );
}
